import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    X, MapPin, Check, AlertTriangle, Loader2, FileText, Upload, ExternalLink,
    Move, ClipboardPaste, ShieldCheck, Stamp, ChevronRight, CircleAlert, Layers, Eye, Paperclip,
    MapPinOff, Receipt, FileX, CircleMinus, Merge, Users, Flag,
} from 'lucide-react';
import { useToast } from '../../../../src/contexts/ToastContext';
import { useAuth } from '../../../../src/contexts/AuthContext';
import {
    carregarRotaCobranca, cadastrosPorUcId, hidratarCadastrosPeloCache,
    faturasAtuaisDasParadas, termosPorUc,
    conferirParada, corrigirCoordenadaUc, liberarRotaCobranca, anexarPdfConferencia,
    abrirAnexoConferencia, tirarParadaDaRota, tirarUcDaRota, juntarParadasDoMesmoCliente,
    type RotaSalva, type ParadaSalva, type FaturaEmAberto,
} from '../../../../src/lib/rotaCobranca';
import { juncoesPendentes, ucsDaParada } from '../../../../src/lib/agrupamentoRota';
import type { CamadaMapa } from './MapaParadaConferencia';
import {
    avaliarMaterial, ehCobrancaAvulsa, faltasDetalhadas, lerCoordenadaColada, pendenciasDaParada,
    situacaoDaRota, temFaturaEquatorial, ucsSemTermo,
    ROTULO_FALTA, ROTULO_PENDENCIA, MIN_JUSTIFICATIVA,
    type AnexosMaterial, type EnderecoSituacao, type FaltaDetalhada, type TermoSituacao, type TipoFalta,
} from '../../../../src/lib/conferenciaRota';
import { enderecoUcTexto, type UcEndereco } from '../../../../src/lib/enderecoUc';
import type { TermoAdesao } from '../../../../src/lib/termoUc';
import { dataBr } from '../../../../src/lib/mensagemCobranca';
import { formatCurrency } from '../utils';

// Conferência da rota de cobrança: o passo que faltava entre montar a rota e
// mandar alguém dirigir até a porta do inadimplente.
//
// A rota nasce em `em_conferencia` e não mostra nada para o cobrador até alguém
// responder, parada a parada: o endereço está certo? ele tem como receber? tem o
// contrato se o cliente disser que nunca assinou? As três respostas ficam
// gravadas com autor e hora — é isso que o gestor pediu que "ficasse registrado".
//
// A tela é um checklist de conferência, não um formulário bonito: coluna de
// carimbos à esquerda, superfície de trabalho à direita, três seções sempre na
// mesma ordem. Quem confere dez paradas faz o mesmo gesto dez vezes.
//
// 16/09/2026 — pedido do Vinicius sobre a rota de 34 paradas:
//   • o que falta aparece em VERMELHO e por extenso, na coluna e no topo da parada;
//   • dá para tirar uma parada (ou uma UC de uma parada) antes de liberar;
//   • clientes repetidos em várias paradas são juntados numa só.

const MapaParadaConferencia = React.lazy(() => import('./MapaParadaConferencia'));

interface Props {
    rotaId: string;
    onFechar: () => void;
    /** Chamado depois que a rota vira `planejada` — a tela de trás recarrega. */
    onLiberada?: () => void;
}

/** As respostas em edição da parada aberta. Só viram banco no "Confirmar parada". */
interface Rascunho {
    enderecoSituacao: EnderecoSituacao | null;
    lat: number | null;
    lng: number | null;
    corrigindo: boolean;
    colado: string;
    /** Y e X digitados à mão — texto, não número: "-16," no meio da digitação
     *  não é número ainda e não pode ser apagado pelo campo. */
    yTexto: string;
    xTexto: string;
    justificativa: string;
    anexos: AnexosMaterial;
    termoSituacao: TermoSituacao | null;
    termoUrl: string | null;
}

const RASCUNHO_VAZIO: Rascunho = {
    enderecoSituacao: null, lat: null, lng: null, corrigindo: false, colado: '',
    yTexto: '', xTexto: '', justificativa: '', anexos: {}, termoSituacao: null, termoUrl: null,
};

/** Alvo do "tirar da rota" esperando confirmação. `ucCodigo` nulo = a parada inteira. */
interface AlvoTirar { paradaId: string; ucCodigo: string | null }

/** O termo é achado por UC: numa parada com duas UCs, cada uma tem o seu. */
const chaveTermo = (paradaId: string, ucCodigo: string) => `${paradaId}|${ucCodigo}`;

const ICONE_FALTA: Record<TipoFalta, React.ReactNode> = {
    endereco: <MapPinOff size={13} />,
    boleto_vigor: <Receipt size={13} />,
    fatura_equatorial: <FileX size={13} />,
    termo: <Stamp size={13} />,
};

/** Seis casas: ~10 cm. Mais que isso é ruído do arrasto, menos já erra a porta. */
const fmtCoord = (v: number | null | undefined): string =>
    v == null || !Number.isFinite(Number(v)) ? '' : Number(v).toFixed(6);

/** "MAX EMILIANO COUTO DA CRUZ" -> "Max Emiliano" — cabe na faixa de junção. */
const nomeCurto = (nome: string | null | undefined): string =>
    String(nome || 'Cliente').split(/[\s(]+/).filter(Boolean).slice(0, 2)
        .map(p => p.charAt(0) + p.slice(1).toLowerCase()).join(' ');

/** Rótulo de seção — o tipo que dá à tela cara de formulário conferido. */
const Rotulo: React.FC<{ icone: React.ReactNode; children: React.ReactNode; estado?: 'ok' | 'pendente' | 'grave' }> =
    ({ icone, children, estado }) => (
        <div className={`flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.12em] ${
            estado === 'ok' ? 'text-[#5F6C37] dark:text-[#9db068]'
                : estado === 'grave' ? 'text-red-600 dark:text-red-400'
                    : estado === 'pendente' ? 'text-amber-600 dark:text-amber-400'
                        : 'text-gray-400 dark:text-gray-500'}`}>
            {icone} {children}
        </div>
    );

/** Carimbo curto de pendência, da coluna de paradas. */
const CarimboFalta: React.FC<{ falta: FaltaDetalhada }> = ({ falta }) => (
    <span title={falta.detalhe} className="px-1 py-px rounded-[3px] text-[8.5px] leading-[13px] font-black uppercase tracking-wider bg-red-50 text-red-700 ring-1 ring-inset ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900">
        {falta.curto}
    </span>
);

const ConferenciaRotaModal: React.FC<Props> = ({ rotaId, onFechar, onLiberada }) => {
    const { addToast } = useToast();
    const { profile } = useAuth();

    const [rota, setRota] = useState<RotaSalva | null>(null);
    // Por id de UC, não por parada: as UCs agrupadas também precisam do cadastro
    // (é dele que sai o segundo número da Equatorial para achar as faturas).
    const [cadastros, setCadastros] = useState<Map<number, UcEndereco>>(new Map());
    const [faturas, setFaturas] = useState<Map<string, FaturaEmAberto[]>>(new Map());
    const [termos, setTermos] = useState<Map<string, TermoAdesao>>(new Map());
    const [carregando, setCarregando] = useState(true);
    const [indice, setIndice] = useState(0);
    const [rascunho, setRascunho] = useState<Rascunho>(RASCUNHO_VAZIO);
    const [salvando, setSalvando] = useState(false);
    const [liberando, setLiberando] = useState(false);
    const [subindo, setSubindo] = useState<string | null>(null);
    const [alvoTirar, setAlvoTirar] = useState<AlvoTirar | null>(null);
    /** Tirando ou juntando paradas — trava os botões que mexem na estrutura. */
    const [mexendo, setMexendo] = useState(false);
    // Fora do rascunho: quem prefere conferir no satélite quer continuar nele ao
    // passar para a próxima parada.
    const [camada, setCamada] = useState<CamadaMapa>('mapa');

    const paradas = rota?.paradas ?? [];
    const parada: ParadaSalva | undefined = paradas[indice];
    const jaLiberada = !!rota?.status && rota.status !== 'em_conferencia';

    const cadastroDe = useCallback(
        (p?: ParadaSalva): UcEndereco | undefined =>
            p?.unidade_consumidora_id != null ? cadastros.get(p.unidade_consumidora_id) : undefined,
        [cadastros]);

    /** `addToast` e `onFechar` num ref, fora das dependências de `carregar`.
     *
     *  Os dois pais montam o modal com arrow inline (`onFechar={() => setConferindo(null)}`),
     *  que é uma função NOVA a cada render deles. Com ela nas deps do `useCallback`,
     *  qualquer re-render do pai trocava a identidade de `carregar`, o
     *  `useEffect([carregar])` disparava e a rota era relida do zero — a tela
     *  voltava para "Lendo a rota…" e o rascunho da parada em andamento ia embora
     *  no meio da conferência. Num ref, a carga passa a depender só de `rotaId`. */
    const doPai = useRef({ addToast, onFechar });
    doPai.current = { addToast, onFechar };

    // -----------------------------------------------------------------------
    // Carga
    // -----------------------------------------------------------------------
    /**
     * Lê a rota e tudo o que a conferência precisa.
     *
     * Sem argumentos é a abertura (tela de carregando e foco na primeira que
     * trava). Com foco é a releitura depois de tirar ou juntar paradas: sem cobrir
     * a tela, e voltando para onde a pessoa estava.
     */
    const carregar = useCallback(async (focarId?: string | null, focarIndice?: number) => {
        const abertura = focarId === undefined && focarIndice === undefined;
        if (abertura) setCarregando(true);
        try {
            const r = await carregarRotaCobranca(rotaId);
            if (!r) { doPai.current.addToast('Rota não encontrada.', 'error'); doPai.current.onFechar(); return; }

            const todas = r.paradas.flatMap(p => ucsDaParada(p).map(u => ({ p, u })));
            // Independentes; em série a abertura demoraria o dobro numa rota de 30.
            const [porId, tms] = await Promise.all([
                // O cache de endereços entra junto: sem ele a conferência marcava
                // "sem localização" em UC cuja coordenada já estava paga e guardada,
                // só porque a coluna do cadastro estava vazia.
                cadastrosPorUcId(todas.map(x => x.u.unidade_consumidora_id))
                    .then(hidratarCadastrosPeloCache),
                termosPorUc(todas.map(({ p, u }) => ({
                    chave: chaveTermo(p.id, u.uc_codigo),
                    ucId: u.unidade_consumidora_id,
                    cpfCnpj: p.cpf_cnpj_cliente,
                }))),
            ]);

            // O saldo é relido AGORA e não o congelado na montagem: entre montar e
            // conferir o cliente pode ter pago, e conferir material de dívida
            // quitada é trabalho jogado fora.
            //
            // Uma leva para a rota inteira, não uma por parada: eram 25+ consultas
            // disparadas juntas, cada uma varrendo as 7.5k faturas com boleto, e a
            // tela ficava presa em "Lendo a rota…". Ver `faturasAtuaisDasParadas`.
            let mapa = new Map<string, FaturaEmAberto[]>();
            try {
                mapa = await faturasAtuaisDasParadas(r.paradas, id => {
                    const cad: any = id != null ? porId.get(id) : null;
                    return [cad?.numero_uc, cad?.outro_numero_uc];
                });
            } catch {
                // Falha na leitura do saldo não pode derrubar a conferência inteira;
                // as paradas aparecem como "não consegui ler" e a tela segue.
                r.paradas.forEach(p => mapa.set(p.id, []));
            }

            setRota(r);
            setCadastros(porId);
            setTermos(tms);
            setFaturas(mapa);

            let alvo = focarId ? r.paradas.findIndex(p => p.id === focarId) : -1;
            if (alvo < 0 && focarIndice != null) alvo = Math.min(focarIndice, r.paradas.length - 1);
            // Abre na primeira que ainda trava — quem reabre a conferência quer
            // continuar de onde parou, não rever o que já assinou.
            if (alvo < 0) alvo = r.paradas.findIndex(p => pendenciasDaParada(p).length > 0);
            setIndice(Math.max(0, alvo));
        } catch (e: any) {
            doPai.current.addToast(e?.message || 'Falha ao carregar a rota.', 'error');
        } finally {
            if (abertura) setCarregando(false);
        }
    }, [rotaId]);

    useEffect(() => { void carregar(); }, [carregar]);

    /** Ao trocar de parada, o rascunho recomeça do que já está gravado nela. */
    useEffect(() => {
        if (!parada) return;
        const uc = cadastroDe(parada);
        const lat = parada.endereco_lat ?? uc?.latitude ?? null;
        const lng = parada.endereco_lng ?? uc?.longitude ?? null;
        setRascunho({
            enderecoSituacao: parada.endereco_situacao ?? null,
            lat,
            lng,
            corrigindo: false,
            colado: '',
            yTexto: fmtCoord(lat),
            xTexto: fmtCoord(lng),
            justificativa: parada.material_justificativa ?? '',
            anexos: parada.material_anexos ?? {},
            termoSituacao: parada.termo_situacao ?? null,
            termoUrl: parada.termo_url ?? null,
        });
        setAlvoTirar(null);
    }, [parada, cadastroDe]);

    // -----------------------------------------------------------------------
    // O que falta — do DADO, não da resposta
    // -----------------------------------------------------------------------
    /**
     * Com `r`, usa o rascunho da parada aberta (pino arrastado, PDF recém-anexado);
     * sem, o que está gravado. "Sem termo" respondido continua sendo falta: libera
     * a parada, mas o cobrador vai sem contrato e precisa saber.
     */
    const faltasDe = useCallback((p: ParadaSalva, r?: Rascunho): FaltaDetalhada[] => {
        const cad = cadastroDe(p);
        const lat = r ? r.lat : (p.endereco_lat ?? cad?.latitude ?? null);
        const lng = r ? r.lng : (p.endereco_lng ?? cad?.longitude ?? null);
        const termoSituacao = r ? r.termoSituacao : p.termo_situacao;
        const ucs = ucsDaParada(p);
        return faltasDetalhadas({
            temEndereco: !!enderecoUcTexto(cad) || !!p.endereco_label,
            temCoordenada: lat != null && lng != null,
            avaliacao: avaliarMaterial(faturas.get(p.id) ?? [], r ? r.anexos : p.material_anexos),
            ucs: ucs.map(u => u.uc_codigo),
            // O termo anexado na conferência cobre a parada inteira.
            ucsSemTermo: termoSituacao === 'anexado' ? [] : ucsSemTermo(ucs.map(u => {
                const c = u.unidade_consumidora_id != null ? cadastros.get(u.unidade_consumidora_id) : null;
                return {
                    codigo: u.uc_codigo,
                    numerosCadastro: [c?.numero_uc, c?.outro_numero_uc],
                    temTermo: !!termos.get(chaveTermo(p.id, u.uc_codigo))?.url,
                };
            })),
        });
    }, [cadastroDe, cadastros, faturas, termos]);

    const faltasPorParada = useMemo(
        () => new Map(paradas.map(p => [p.id, faltasDe(p)] as const)),
        [paradas, faltasDe]);

    const juncoes = useMemo(
        () => (jaLiberada ? [] : juncoesPendentes(paradas)),
        [paradas, jaLiberada]);

    // -----------------------------------------------------------------------
    // Derivados da parada aberta
    // -----------------------------------------------------------------------
    const cadastro = cadastroDe(parada);
    const faturasDaParada = useMemo(
        () => (parada ? (faturas.get(parada.id) ?? []) : []),
        [parada, faturas]);
    const ucsAbertas = useMemo(() => (parada ? ucsDaParada(parada) : []), [parada]);
    const grupo = ucsAbertas.length > 1;
    const termoDaUc = (uc: string) => (parada ? termos.get(chaveTermo(parada.id, uc)) : undefined);
    const termoPrincipal = parada ? termos.get(chaveTermo(parada.id, parada.uc_codigo)) : undefined;
    const todosComTermo = ucsAbertas.length > 0 && ucsAbertas.every(u => !!termoDaUc(u.uc_codigo)?.url);

    const avaliacao = useMemo(
        () => avaliarMaterial(faturasDaParada, rascunho.anexos),
        [faturasDaParada, rascunho.anexos]);
    // A mais recente é por UC: cada UC juntada tem a sua fatura do mês.
    const idsRecentes = useMemo(() => {
        const ultimaPorUc = new Map<string, string>();
        faturasDaParada.forEach(f => ultimaPorUc.set(String(f.uc_ref ?? ''), String(f.id)));
        return new Set(ultimaPorUc.values());
    }, [faturasDaParada]);
    const faltasAbertas = parada ? faltasDe(parada, rascunho) : [];

    const materialSituacao = avaliacao.completo ? 'completo' : 'justificado';
    const justificativaOk = avaliacao.completo
        || rascunho.justificativa.trim().length >= MIN_JUSTIFICATIVA;
    const podeConfirmar = !!rascunho.enderecoSituacao && justificativaOk && !!rascunho.termoSituacao;

    const situacao = useMemo(() => situacaoDaRota(paradas), [paradas]);

    // Termo achado pelo sistema em TODAS as UCs responde a seção sozinho: perguntar
    // o que já se sabe é o tipo de atrito que faz a pessoa clicar em qualquer coisa.
    useEffect(() => {
        const url = termoPrincipal?.url;
        if (!todosComTermo || !url) return;
        setRascunho(r => (r.termoSituacao ? r : { ...r, termoSituacao: 'tem', termoUrl: url }));
    }, [todosComTermo, termoPrincipal, rascunho.termoSituacao]);

    // -----------------------------------------------------------------------
    // Ações
    // -----------------------------------------------------------------------
    const subirArquivo = async (
        tipo: 'fatura' | 'termo',
        arquivo: File,
        faturaId?: string,
    ) => {
        if (!parada) return;
        const chave = tipo === 'termo' ? 'termo' : String(faturaId);
        setSubindo(chave);
        try {
            const caminho = await anexarPdfConferencia(rotaId, parada.id, tipo, arquivo, faturaId);
            setRascunho(r => tipo === 'termo'
                ? { ...r, termoSituacao: 'anexado', termoUrl: caminho }
                : { ...r, anexos: { ...r.anexos, [String(faturaId)]: caminho } });
            addToast('Arquivo anexado.', 'success');
        } catch (e: any) {
            addToast(e?.message || 'Não consegui subir o arquivo.', 'error');
        } finally {
            setSubindo(null);
        }
    };

    /** Todo caminho de correção termina aqui: arrasto, colagem e Y/X digitados. */
    const aplicarCoordenada = (lat: number, lng: number, extra?: Partial<Rascunho>) =>
        setRascunho(r => ({
            ...r, ...extra, lat, lng, enderecoSituacao: 'corrigido',
            yTexto: fmtCoord(lat), xTexto: fmtCoord(lng),
        }));

    const aplicarColado = () => {
        const c = lerCoordenadaColada(rascunho.colado);
        if (!c) {
            addToast('Não reconheci a coordenada. Cole algo como "-16.6799, -49.2550".', 'error');
            return;
        }
        aplicarCoordenada(c.lat, c.lng, { colado: '' });
        addToast('Coordenada aplicada. Confira o pino no mapa.', 'success');
    };

    /**
     * Y e X digitados à mão. Passam pelo MESMO leitor da colagem, de propósito: a
     * recusa de par invertido e de ponto fora do Brasil vale igual para quem
     * digita — e trocar latitude com longitude é o erro mais comum de quem digita.
     */
    const aplicarXY = () => {
        const c = lerCoordenadaColada(`${rascunho.yTexto}; ${rascunho.xTexto}`);
        if (!c) {
            addToast('Y é a latitude (em Goiás fica entre -19 e -12) e X a longitude (entre -53 e -45). Confira se não estão trocados.', 'error');
            return;
        }
        aplicarCoordenada(c.lat, c.lng);
        addToast('Pino movido para o Y e X digitados. Confira no mapa.', 'success');
    };

    const confirmarParada = async () => {
        if (!parada || !profile?.id || !podeConfirmar) return;
        setSalvando(true);
        try {
            // A correção vai para o CADASTRO, não só para a parada: o endereço
            // errado é da UC e o próximo que montar rota com ela erraria de novo.
            if (rascunho.enderecoSituacao === 'corrigido'
                && parada.unidade_consumidora_id != null
                && rascunho.lat != null && rascunho.lng != null) {
                await corrigirCoordenadaUc(
                    parada.unidade_consumidora_id, rascunho.lat, rascunho.lng,
                    enderecoUcTexto(cadastro) || null);
            }

            await conferirParada(parada.id, {
                enderecoSituacao: rascunho.enderecoSituacao!,
                enderecoLat: rascunho.lat,
                enderecoLng: rascunho.lng,
                enderecoLabel: enderecoUcTexto(cadastro) || null,
                materialSituacao,
                // Congela o que faltava AGORA: é a prova do que a pessoa viu ao
                // assinar, não o estado de amanhã.
                materialFaltando: avaliacao.pendencias,
                materialJustificativa: avaliacao.completo ? null : rascunho.justificativa.trim(),
                materialAnexos: Object.keys(rascunho.anexos).length > 0 ? rascunho.anexos : null,
                termoSituacao: rascunho.termoSituacao!,
                termoUrl: rascunho.termoUrl,
                conferidaPor: profile.id,
            });

            // Atualiza em memória em vez de reler tudo: releitura descartaria os
            // anexos já subidos das outras paradas e piscaria a tela inteira.
            setRota(r => r && ({
                ...r,
                paradas: r.paradas.map(p => p.id !== parada.id ? p : {
                    ...p,
                    endereco_situacao: rascunho.enderecoSituacao,
                    endereco_lat: rascunho.lat,
                    endereco_lng: rascunho.lng,
                    material_situacao: materialSituacao,
                    material_justificativa: avaliacao.completo ? null : rascunho.justificativa.trim(),
                    material_anexos: rascunho.anexos,
                    termo_situacao: rascunho.termoSituacao,
                    termo_url: rascunho.termoUrl,
                    conferida_em: new Date().toISOString(),
                    conferida_por: profile.id,
                }),
            }));

            const proxima = paradas.findIndex(
                (p, i) => i !== indice && pendenciasDaParada(p).length > 0 && p.id !== parada.id);
            if (proxima >= 0) setIndice(proxima);
            else addToast('Todas as paradas conferidas. Pode liberar a rota.', 'success');
        } catch (e: any) {
            addToast(e?.message || 'Falha ao gravar a conferência.', 'error');
        } finally {
            setSalvando(false);
        }
    };

    /**
     * Tira a parada inteira ou uma UC dela. Sempre relê: a posição das outras
     * paradas muda, e a parada que perdeu UC precisa ser conferida de novo.
     */
    const executarTirar = async () => {
        if (!rota || !alvoTirar) return;
        const alvo = paradas.find(p => p.id === alvoTirar.paradaId);
        if (!alvo) { setAlvoTirar(null); return; }
        const inteira = alvoTirar.ucCodigo == null || ucsDaParada(alvo).length === 1;
        const posicaoAntes = paradas.findIndex(p => p.id === alvo.id);
        setMexendo(true);
        try {
            if (inteira) {
                await tirarParadaDaRota(rota.id, alvo.id);
                addToast(`${nomeCurto(alvo.cliente_nome)} saiu da rota. As paradas seguintes subiram uma posição.`, 'success');
            } else {
                await tirarUcDaRota(rota.id, alvo, alvoTirar.ucCodigo!);
                addToast(`UC ${alvoTirar.ucCodigo} saiu desta parada. Confira a parada de novo.`, 'success');
            }
            setAlvoTirar(null);
            await carregar(inteira ? null : alvo.id, posicaoAntes);
        } catch (e: any) {
            addToast(e?.message || 'Não consegui tirar da rota.', 'error');
        } finally {
            setMexendo(false);
        }
    };

    const juntar = async () => {
        if (!rota) return;
        setMexendo(true);
        try {
            const menos = await juntarParadasDoMesmoCliente(rota.id, paradas);
            addToast(
                `${menos} parada${menos > 1 ? 's' : ''} a menos: cada cliente é visitado uma vez e paga todas as UCs lá.`,
                'success');
            await carregar(parada?.id ?? null, indice);
        } catch (e: any) {
            addToast(e?.message || 'Não consegui juntar as paradas.', 'error');
        } finally {
            setMexendo(false);
        }
    };

    const liberar = async () => {
        if (!rota || !profile?.id) return;
        setLiberando(true);
        try {
            await liberarRotaCobranca(rota.id, profile.id);
            addToast(`Rota liberada para ${rota.responsavel_nome || 'o responsável'}.`, 'success');
            onLiberada?.();
            onFechar();
        } catch (e: any) {
            addToast(e?.message || 'Falha ao liberar a rota.', 'error');
        } finally {
            setLiberando(false);
        }
    };

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    const enderecoTexto = enderecoUcTexto(cadastro) || parada?.endereco_label || '';
    const temCoordenada = rascunho.lat != null && rascunho.lng != null;
    const comFalta = paradas.filter(p => (faltasPorParada.get(p.id) ?? []).length > 0).length;
    const alvoAberto = alvoTirar && parada && alvoTirar.paradaId === parada.id ? alvoTirar : null;

    return (
        <div className="fixed inset-0 z-50 bg-gray-900/70 backdrop-blur-sm flex items-center justify-center p-0 sm:p-4">
            <div className="bg-white dark:bg-gray-900 w-full h-full sm:h-[94vh] sm:max-w-6xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden">

                {/* Cabeçalho — o "documento" que está sendo assinado */}
                <header className="shrink-0 px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <ShieldCheck size={16} className="text-[#5F6C37] shrink-0" />
                            <h2 className="text-base font-black text-gray-800 dark:text-gray-100 tracking-tight truncate">
                                Conferir antes de liberar
                            </h2>
                        </div>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                            {rota?.nome || 'Rota de cobrança'} · {dataBr(rota?.data)}
                            {rota?.responsavel_nome && <> · para <strong className="text-gray-700 dark:text-gray-200">{rota.responsavel_nome}</strong></>}
                            {rota?.criador_nome && <> · montada por {rota.criador_nome}</>}
                            {rota?.destino_label && (
                                <span className="inline-flex items-center gap-1 ml-1">
                                    · <Flag size={10} className="text-[#5F6C37]" /> chega em {rota.destino_label}
                                </span>
                            )}
                        </p>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                        {/* Carimbo de progresso */}
                        <div className="hidden sm:flex flex-col items-end">
                            <span className="text-[10px] font-black uppercase tracking-[0.12em] text-gray-400">Conferidas</span>
                            <span className="text-lg font-black tabular-nums leading-none text-gray-800 dark:text-gray-100">
                                {situacao.conferidas}<span className="text-gray-400 dark:text-gray-500">/{situacao.total}</span>
                            </span>
                        </div>
                        <button onClick={onFechar} className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                            <X size={18} />
                        </button>
                    </div>
                </header>

                {/* Barra de progresso fina — leitura periférica do avanço */}
                <div className="shrink-0 h-1 bg-gray-100 dark:bg-gray-800">
                    <div
                        className="h-full bg-[#5F6C37] transition-[width] duration-500 ease-out"
                        style={{ width: `${situacao.total ? (situacao.conferidas / situacao.total) * 100 : 0}%` }}
                    />
                </div>

                {/* Clientes repetidos — a rota de 34 paradas tinha cinco. Vem antes de
                    tudo porque juntar reabre a conferência das paradas envolvidas:
                    melhor fazer primeiro do que conferir duas vezes. */}
                {!carregando && juncoes.length > 0 && (
                    <div className="shrink-0 px-5 py-2.5 border-b border-[#5F6C37]/20 bg-[#5F6C37]/[0.06] dark:bg-[#5F6C37]/10 flex flex-wrap items-center gap-x-4 gap-y-2">
                        <Merge size={16} className="text-[#5F6C37] dark:text-[#9db068] shrink-0" />
                        <p className="flex-1 min-w-[240px] text-[11px] text-gray-700 dark:text-gray-200 leading-snug">
                            <strong>
                                {juncoes.length === 1 ? '1 cliente aparece' : `${juncoes.length} clientes aparecem`} em mais de uma parada
                            </strong>
                            {' — '}
                            {juncoes.map(j => `${nomeCurto(j.manter.cliente_nome)} (${j.apagar.length + 1})`).join(', ')}.
                            {' '}Juntando, o cobrador vai uma vez só e cobra todas as UCs lá.
                        </p>
                        <button
                            onClick={juntar}
                            disabled={mexendo}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#5F6C37] hover:bg-[#4a5429] text-white text-[11px] font-bold transition-colors disabled:opacity-50 shrink-0"
                        >
                            {mexendo ? <Loader2 size={13} className="animate-spin" /> : <Merge size={13} />}
                            Juntar numa parada por cliente
                        </button>
                    </div>
                )}

                {carregando ? (
                    <div className="flex-1 flex items-center justify-center text-gray-400 gap-2">
                        <Loader2 size={18} className="animate-spin" /> <span className="text-sm">Lendo a rota…</span>
                    </div>
                ) : (
                    <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[272px_1fr]">

                        {/* Coluna de carimbos */}
                        <nav className="hidden md:block border-r border-gray-200 dark:border-gray-700 overflow-y-auto bg-gray-50/60 dark:bg-gray-900/40">
                            {paradas.map((p, i) => {
                                const pend = pendenciasDaParada(p);
                                const faltas = faltasPorParada.get(p.id) ?? [];
                                const nUcs = ucsDaParada(p).length;
                                const ativa = i === indice;
                                return (
                                    <button
                                        key={p.id}
                                        onClick={() => setIndice(i)}
                                        className={`w-full text-left px-3 py-2.5 border-b border-gray-100 dark:border-gray-800 flex items-start gap-2.5 transition-colors ${
                                            ativa ? 'bg-white dark:bg-gray-800 shadow-[inset_3px_0_0_#5F6C37]'
                                                : 'hover:bg-white/70 dark:hover:bg-gray-800/50'}`}
                                    >
                                        <span className={`mt-0.5 w-6 h-6 rounded-full shrink-0 flex items-center justify-center text-[10px] font-black transition-transform ${
                                            pend.length === 0
                                                ? 'bg-[#5F6C37] text-white scale-100'
                                                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'}`}>
                                            {pend.length === 0 ? <Check size={13} strokeWidth={3} /> : p.posicao}
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block text-[11px] font-bold text-gray-700 dark:text-gray-200 truncate">
                                                {p.cliente_nome || p.uc_codigo}
                                            </span>
                                            <span className="block text-[10px] text-gray-400 tabular-nums truncate">
                                                UC {p.uc_codigo}{nUcs > 1 && ` +${nUcs - 1}`} · {formatCurrency(Number(p.valor_total || 0))}
                                            </span>
                                            {(nUcs > 1 || faltas.length > 0) && (
                                                <span className="mt-1 flex flex-wrap gap-1">
                                                    {nUcs > 1 && (
                                                        <span className="flex items-center gap-0.5 px-1 py-px rounded-[3px] text-[8.5px] leading-[13px] font-black uppercase tracking-wider bg-[#5F6C37]/10 text-[#5F6C37] dark:text-[#9db068]">
                                                            <Users size={9} /> {nUcs} UCs
                                                        </span>
                                                    )}
                                                    {faltas.map(f => <CarimboFalta key={f.tipo} falta={f} />)}
                                                </span>
                                            )}
                                        </span>
                                    </button>
                                );
                            })}
                        </nav>

                        {/* Superfície de trabalho */}
                        <div className="overflow-y-auto">
                            {!parada ? (
                                <div className="p-8 text-center text-sm text-gray-400">Esta rota não tem paradas.</div>
                            ) : (
                                <div className="p-5 space-y-5 max-w-3xl">

                                    {/* Identificação da parada */}
                                    <div className="md:hidden flex items-center gap-2 text-[11px] font-bold text-gray-500">
                                        Parada {parada.posicao} de {paradas.length}
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <h3 className="text-lg font-black text-gray-800 dark:text-gray-100 tracking-tight leading-tight">
                                                    {parada.cliente_nome || 'Sem nome no cadastro'}
                                                </h3>
                                                <p className="text-[11px] text-gray-500 tabular-nums mt-0.5">
                                                    {grupo
                                                        ? <>{ucsAbertas.length} UCs · {formatCurrency(Number(parada.valor_total || 0))} no total</>
                                                        : <>UC {parada.uc_codigo}</>}
                                                    {parada.cpf_cnpj_cliente && <> · {parada.cpf_cnpj_cliente}</>}
                                                </p>
                                            </div>
                                            {!jaLiberada && (
                                                <button
                                                    onClick={() => setAlvoTirar({ paradaId: parada.id, ucCodigo: null })}
                                                    disabled={mexendo}
                                                    className="shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-[11px] font-bold text-gray-500 dark:text-gray-400 hover:text-red-700 hover:border-red-200 hover:bg-red-50 dark:hover:bg-red-950/30 dark:hover:text-red-300 transition-colors disabled:opacity-40"
                                                >
                                                    <CircleMinus size={13} /> Tirar da rota
                                                </button>
                                            )}
                                        </div>

                                        {alvoAberto && (
                                            <div className="rounded-xl border border-red-300 dark:border-red-900 bg-red-50 dark:bg-red-950/30 px-4 py-3 flex flex-wrap items-center gap-3">
                                                <p className="flex-1 min-w-[220px] text-[12px] text-red-800 dark:text-red-200 leading-snug">
                                                    {alvoAberto.ucCodigo == null || !grupo ? (
                                                        <>
                                                            Tirar <strong>{parada.cliente_nome || `UC ${parada.uc_codigo}`}</strong> da rota
                                                            {grupo && <> (as {ucsAbertas.length} UCs)</>}? O cobrador não passa mais lá
                                                            e as paradas seguintes sobem uma posição.
                                                        </>
                                                    ) : (
                                                        <>
                                                            Tirar a <strong>UC {alvoAberto.ucCodigo}</strong> desta parada?{' '}
                                                            {alvoAberto.ucCodigo === parada.uc_codigo
                                                                ? 'É a UC do endereço da visita: a próxima assume e o endereço precisa ser conferido de novo.'
                                                                : 'O valor dela sai do total cobrado aqui.'}
                                                        </>
                                                    )}
                                                </p>
                                                <button
                                                    onClick={() => setAlvoTirar(null)}
                                                    disabled={mexendo}
                                                    className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-gray-600 dark:text-gray-300 hover:bg-white/70 dark:hover:bg-gray-800"
                                                >
                                                    Cancelar
                                                </button>
                                                <button
                                                    onClick={executarTirar}
                                                    disabled={mexendo}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-[11px] font-bold transition-colors disabled:opacity-50"
                                                >
                                                    {mexendo ? <Loader2 size={12} className="animate-spin" /> : <CircleMinus size={12} />}
                                                    Tirar
                                                </button>
                                            </div>
                                        )}

                                        {/* O que falta, com todas as letras. Vermelho mesmo depois de
                                            respondido: "não existe termo" libera, mas o cobrador vai
                                            sem contrato e precisa saber antes de bater na porta. */}
                                        {faltasAbertas.length > 0 ? (
                                            <section aria-label="O que falta nesta parada" className="rounded-xl border border-red-200 dark:border-red-900/60 bg-red-50/80 dark:bg-red-950/20 overflow-hidden">
                                                <div className="px-4 py-2 flex items-center gap-2 border-b border-red-200/80 dark:border-red-900/50">
                                                    <CircleAlert size={13} className="text-red-600 dark:text-red-400" />
                                                    <span className="text-[10px] font-black uppercase tracking-[0.12em] text-red-700 dark:text-red-300">
                                                        Falta nesta parada
                                                    </span>
                                                    <span className="ml-auto text-[10px] font-black tabular-nums text-red-700 dark:text-red-300">
                                                        {faltasAbertas.length}
                                                    </span>
                                                </div>
                                                <ul className="divide-y divide-red-100 dark:divide-red-900/40">
                                                    {faltasAbertas.map(f => (
                                                        <li key={f.tipo} className="px-4 py-2 flex items-start gap-2.5">
                                                            <span className="mt-px text-red-600 dark:text-red-400 shrink-0">{ICONE_FALTA[f.tipo]}</span>
                                                            <span className="text-[12px] font-bold text-red-800 dark:text-red-200 leading-snug">
                                                                {f.detalhe}
                                                                {f.tipo === 'termo' && rascunho.termoSituacao === 'sem_termo' && (
                                                                    <span className="font-medium text-red-700/80 dark:text-red-300/80"> · respondido: não existe, o cobrador vai sem contrato</span>
                                                                )}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </section>
                                        ) : (
                                            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#5F6C37]/[0.07] text-[11px] font-bold text-[#5F6C37] dark:text-[#9db068]">
                                                <Check size={13} strokeWidth={3} /> Nada faltando: localização, boleto, fatura e termo em ordem.
                                            </div>
                                        )}
                                    </div>

                                    {/* ── UCs juntadas ────────────────────────────── */}
                                    {grupo && (
                                        <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-2">
                                            <Rotulo icone={<Users size={12} />} estado="ok">
                                                {ucsAbertas.length} UCs do mesmo cliente nesta parada
                                            </Rotulo>
                                            <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
                                                O cobrador vai no endereço da primeira e cobra lá o total de{' '}
                                                <strong className="text-gray-700 dark:text-gray-200 tabular-nums">{formatCurrency(Number(parada.valor_total || 0))}</strong>.
                                            </p>
                                            <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                                                {ucsAbertas.map((u, i) => (
                                                    <li key={u.uc_codigo} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                                                        <span className="text-[11px] font-black text-gray-800 dark:text-gray-100 tabular-nums">UC {u.uc_codigo}</span>
                                                        {i === 0 && (
                                                            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900">
                                                                <MapPin size={9} /> endereço da visita
                                                            </span>
                                                        )}
                                                        <span className="text-[10px] text-gray-500 tabular-nums">
                                                            {u.qtd_faturas} fatura{u.qtd_faturas === 1 ? '' : 's'}
                                                        </span>
                                                        <span className="ml-auto text-[11px] font-black text-gray-800 dark:text-gray-100 tabular-nums">
                                                            {formatCurrency(u.valor_total)}
                                                        </span>
                                                        {!jaLiberada && (
                                                            <button
                                                                onClick={() => setAlvoTirar({ paradaId: parada.id, ucCodigo: u.uc_codigo })}
                                                                disabled={mexendo}
                                                                title="Tirar esta UC da parada"
                                                                aria-label={`Tirar a UC ${u.uc_codigo} da parada`}
                                                                className="p-1 rounded text-gray-300 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors disabled:opacity-40"
                                                            >
                                                                <CircleMinus size={14} />
                                                            </button>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        </section>
                                    )}

                                    {/* ── 1. ENDEREÇO ─────────────────────────────── */}
                                    <section className={`rounded-xl border overflow-hidden ${temCoordenada ? 'border-gray-200 dark:border-gray-700' : 'border-red-200 dark:border-red-900/60'}`}>
                                        <div className="px-4 pt-3.5 pb-3">
                                            <Rotulo
                                                icone={<MapPin size={12} />}
                                                estado={!temCoordenada ? 'grave' : rascunho.enderecoSituacao ? 'ok' : 'pendente'}
                                            >
                                                1 · Endereço {rascunho.enderecoSituacao === 'confirmado' ? '· confirmado'
                                                    : rascunho.enderecoSituacao === 'corrigido' ? '· corrigido' : ''}
                                                {grupo && <span className="normal-case tracking-normal font-bold text-gray-400"> · da UC {parada.uc_codigo}</span>}
                                            </Rotulo>
                                            <p className="mt-2 text-sm text-gray-700 dark:text-gray-200 leading-snug">
                                                {enderecoTexto || <span className="text-red-600 dark:text-red-400 font-bold">Sem endereço no cadastro</span>}
                                            </p>
                                            {temCoordenada && (
                                                <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] font-mono text-gray-500 dark:text-gray-400 tabular-nums">
                                                    <span><span className="font-sans font-black text-gray-400 mr-1">Y</span>{fmtCoord(rascunho.lat)}</span>
                                                    <span><span className="font-sans font-black text-gray-400 mr-1">X</span>{fmtCoord(rascunho.lng)}</span>
                                                    {cadastro?.geocode_status && (
                                                        <span className="font-sans uppercase tracking-wider text-gray-400">nível {cadastro.geocode_status}</span>
                                                    )}
                                                </p>
                                            )}
                                        </div>

                                        {temCoordenada ? (
                                            <div className="h-64 relative border-y border-gray-200 dark:border-gray-700">
                                                <Suspense fallback={<div className="h-full flex items-center justify-center text-xs text-gray-400"><Loader2 size={14} className="animate-spin mr-2" /> abrindo o mapa…</div>}>
                                                    <MapaParadaConferencia
                                                        lat={Number(rascunho.lat)}
                                                        lng={Number(rascunho.lng)}
                                                        arrastavel={rascunho.corrigindo}
                                                        camada={camada}
                                                        onMover={(lat, lng) => aplicarCoordenada(lat, lng)}
                                                    />
                                                </Suspense>
                                                {rascunho.corrigindo && (
                                                    <div className="absolute top-2 left-2 z-[400] px-2.5 py-1 rounded-full bg-gray-900/85 text-white text-[10px] font-bold flex items-center gap-1.5 pointer-events-none">
                                                        <Move size={11} /> arraste o pino até a porta
                                                    </div>
                                                )}
                                                {/* Mapa × satélite. No mapa de ruas o pino no meio da quadra
                                                    parece certo; na foto dá para ver em que lote ele caiu. */}
                                                <div className="absolute top-2 right-2 z-[400] flex rounded-lg overflow-hidden shadow-md border border-white/70">
                                                    {(['mapa', 'satelite'] as const).map(c => (
                                                        <button
                                                            key={c}
                                                            onClick={() => setCamada(c)}
                                                            className={`px-2.5 py-1 text-[10px] font-bold flex items-center gap-1 transition-colors ${
                                                                camada === c ? 'bg-gray-900 text-white' : 'bg-white/90 text-gray-600 hover:bg-white'}`}
                                                        >
                                                            {c === 'satelite' && <Layers size={11} />}
                                                            {c === 'mapa' ? 'Mapa' : 'Satélite'}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="px-4 py-6 bg-red-50 dark:bg-red-950/20 border-y border-red-200 dark:border-red-900/50 text-center">
                                                <MapPinOff size={16} className="mx-auto text-red-600 mb-1.5" />
                                                <p className="text-[11px] text-red-800 dark:text-red-300 font-bold">
                                                    Falta a localização desta UC. Cole a do Google Maps abaixo para o cobrador conseguir navegar.
                                                </p>
                                            </div>
                                        )}

                                        <div className="px-4 py-3 space-y-2.5">
                                            <div className="flex flex-wrap gap-2">
                                                <button
                                                    onClick={() => setRascunho(r => ({
                                                        ...r,
                                                        // Depois de arrastar, "está certo" quer dizer "o pino
                                                        // corrigido está certo". Virar 'confirmado' aqui jogaria
                                                        // fora a correção: ela não iria para o cadastro e o
                                                        // cobrador seguiria para o ponto antigo.
                                                        enderecoSituacao: r.enderecoSituacao === 'corrigido' ? 'corrigido' : 'confirmado',
                                                        corrigindo: false,
                                                    }))}
                                                    disabled={!temCoordenada}
                                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-colors disabled:opacity-40 ${
                                                        rascunho.enderecoSituacao && !rascunho.corrigindo
                                                            ? 'bg-[#5F6C37] text-white'
                                                            : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                                                >
                                                    <Check size={13} /> Está certo
                                                </button>
                                                <button
                                                    onClick={() => setRascunho(r => ({ ...r, corrigindo: !r.corrigindo }))}
                                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-colors ${
                                                        rascunho.corrigindo
                                                            ? 'bg-amber-500 text-white'
                                                            : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                                                >
                                                    <Move size={13} /> {rascunho.corrigindo ? 'Corrigindo…' : 'Está errado'}
                                                </button>
                                                {rascunho.enderecoSituacao === 'corrigido' && (
                                                    <button
                                                        onClick={() => {
                                                            // Volta ao pino do cadastro, como estava ao abrir.
                                                            const lat = cadastro?.latitude ?? null;
                                                            const lng = cadastro?.longitude ?? null;
                                                            setRascunho(r => ({
                                                                ...r, lat, lng, yTexto: fmtCoord(lat), xTexto: fmtCoord(lng),
                                                                enderecoSituacao: null, corrigindo: false,
                                                            }));
                                                        }}
                                                        className="px-1.5 py-1.5 text-[11px] font-bold text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:underline underline-offset-2"
                                                    >
                                                        desfazer correção
                                                    </button>
                                                )}
                                                {temCoordenada && (
                                                    <span className="sm:ml-auto flex flex-wrap gap-2">
                                                        <a
                                                            href={`https://www.google.com/maps/search/?api=1&query=${rascunho.lat},${rascunho.lng}`}
                                                            target="_blank" rel="noopener noreferrer"
                                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-[11px] font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-50"
                                                        >
                                                            <ExternalLink size={12} /> Abrir no Maps
                                                        </a>
                                                        {/* A fachada é o que o cobrador vai ver da rua — confirma
                                                            a casa melhor que qualquer pino. */}
                                                        <a
                                                            href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${rascunho.lat},${rascunho.lng}`}
                                                            target="_blank" rel="noopener noreferrer"
                                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-[11px] font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-50"
                                                        >
                                                            <Eye size={12} /> Street View
                                                        </a>
                                                    </span>
                                                )}
                                            </div>

                                            {(rascunho.corrigindo || !temCoordenada) && (
                                                <div className="rounded-lg bg-amber-50/70 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-900/40 p-3 space-y-2.5">
                                                    <p className="text-[11px] text-amber-800 dark:text-amber-300 leading-snug">
                                                        {temCoordenada ? 'Arraste o pino até a porta ou digite a posição certa.' : 'Digite a posição do endereço.'}
                                                        {' '}No Google Maps, clique com o botão direito (no celular, segure o dedo)
                                                        em cima da casa: o primeiro número é o <strong>Y</strong>, o segundo é o <strong>X</strong>.
                                                    </p>
                                                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                                                        <label className="block">
                                                            <span className="block text-[10px] font-black uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400 mb-1">Y · latitude</span>
                                                            <input
                                                                inputMode="decimal"
                                                                value={rascunho.yTexto}
                                                                onChange={e => setRascunho(r => ({ ...r, yTexto: e.target.value }))}
                                                                onKeyDown={e => { if (e.key === 'Enter') aplicarXY(); }}
                                                                placeholder="-16.679900"
                                                                className="w-full px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs font-mono tabular-nums outline-none focus:ring-2 focus:ring-[#5F6C37]"
                                                            />
                                                        </label>
                                                        <label className="block">
                                                            <span className="block text-[10px] font-black uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400 mb-1">X · longitude</span>
                                                            <input
                                                                inputMode="decimal"
                                                                value={rascunho.xTexto}
                                                                onChange={e => setRascunho(r => ({ ...r, xTexto: e.target.value }))}
                                                                onKeyDown={e => { if (e.key === 'Enter') aplicarXY(); }}
                                                                placeholder="-49.255000"
                                                                className="w-full px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs font-mono tabular-nums outline-none focus:ring-2 focus:ring-[#5F6C37]"
                                                            />
                                                        </label>
                                                        <button
                                                            onClick={aplicarXY}
                                                            disabled={!rascunho.yTexto.trim() || !rascunho.xTexto.trim()}
                                                            className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 dark:bg-gray-700 text-white text-[11px] font-bold disabled:opacity-40"
                                                        >
                                                            <MapPin size={12} /> Mover pino
                                                        </button>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <input
                                                            value={rascunho.colado}
                                                            onChange={e => setRascunho(r => ({ ...r, colado: e.target.value }))}
                                                            onKeyDown={e => { if (e.key === 'Enter') aplicarColado(); }}
                                                            placeholder="ou cole o link / as coordenadas copiadas do Maps"
                                                            className="flex-1 min-w-0 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs outline-none focus:ring-2 focus:ring-[#5F6C37]"
                                                        />
                                                        <button
                                                            onClick={aplicarColado}
                                                            disabled={!rascunho.colado.trim()}
                                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 dark:bg-gray-700 text-white text-[11px] font-bold disabled:opacity-40"
                                                        >
                                                            <ClipboardPaste size={12} /> Usar
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </section>

                                    {/* ── 2. MATERIAL ─────────────────────────────── */}
                                    <section className={`rounded-xl border p-4 space-y-3 ${avaliacao.completo ? 'border-gray-200 dark:border-gray-700' : 'border-red-200 dark:border-red-900/60'}`}>
                                        <Rotulo
                                            icone={<FileText size={12} />}
                                            estado={avaliacao.completo ? 'ok' : 'grave'}
                                        >
                                            2 · Material de cobrança
                                            {avaliacao.completo && ' · completo'}
                                        </Rotulo>

                                        {avaliacao.semFaturas ? (
                                            <p className="text-xs text-[#5F6C37] dark:text-[#9db068] font-semibold">
                                                Nada em aberto hoje — o cliente pagou depois que a rota foi montada.
                                            </p>
                                        ) : (
                                            <div className="space-y-1.5">
                                                {faturasDaParada.map(f => {
                                                    const pend = avaliacao.pendencias.find(p => p.faturaId === String(f.id));
                                                    const recente = idsRecentes.has(String(f.id));
                                                    // Parcela de acordo/acerto: não existe fatura da Equatorial
                                                    // dela para anexar, e dizer "boleto e fatura" seria mentira.
                                                    const avulsaSemFatura = ehCobrancaAvulsa(f)
                                                        && !temFaturaEquatorial(f, rascunho.anexos);
                                                    return (
                                                        <div key={f.id} className={`px-3 py-2 rounded-lg border flex flex-wrap items-center gap-x-3 gap-y-1.5 ${
                                                            !pend ? 'border-gray-100 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-800/40'
                                                                : recente ? 'border-red-300 dark:border-red-900/60 bg-red-50 dark:bg-red-900/10'
                                                                    : 'border-red-200 dark:border-red-900/40 bg-white dark:bg-gray-900'}`}>
                                                            {grupo && f.uc_ref ? (
                                                                <span className="text-[10px] font-black text-gray-500 dark:text-gray-400 tabular-nums">
                                                                    UC {f.uc_ref}
                                                                </span>
                                                            ) : null}
                                                            <span className="text-[11px] font-bold text-gray-700 dark:text-gray-200 tabular-nums">
                                                                {f.mes_referencia || '—'} · venc. {dataBr(f.vencimento)}
                                                            </span>
                                                            <span className="text-[11px] font-black text-gray-800 dark:text-gray-100 tabular-nums">
                                                                {formatCurrency(f.valor)}
                                                            </span>
                                                            {recente && (
                                                                <span className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900">
                                                                    mais recente
                                                                </span>
                                                            )}
                                                            {ehCobrancaAvulsa(f) && (
                                                                <span
                                                                    title="Cobrança lançada pela Vigor (parcela de acordo, acerto). Não vem de uma fatura da Equatorial."
                                                                    className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700"
                                                                >
                                                                    cobrança avulsa
                                                                </span>
                                                            )}

                                                            {!pend ? (
                                                                <span className="ml-auto flex items-center gap-2.5 text-[10px] font-bold text-[#5F6C37] dark:text-[#9db068]">
                                                                    {/* O PDF que resolveu a pendência precisa poder ser aberto:
                                                                        subir o arquivo errado é o erro que só se vê abrindo. */}
                                                                    {rascunho.anexos[String(f.id)] && (
                                                                        <button
                                                                            onClick={() => void abrirAnexoConferencia(rascunho.anexos[String(f.id)])
                                                                                .catch((e: any) => addToast(e?.message || 'Não consegui abrir o anexo.', 'error'))}
                                                                            className="flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:underline underline-offset-2"
                                                                        >
                                                                            <Paperclip size={11} /> fatura anexada
                                                                        </button>
                                                                    )}
                                                                    <span className="flex items-center gap-1">
                                                                        <Check size={12} strokeWidth={3} />
                                                                        {avulsaSemFatura ? 'boleto · não tem fatura da Equatorial' : 'boleto e fatura'}
                                                                    </span>
                                                                </span>
                                                            ) : (
                                                                <span className="ml-auto flex flex-wrap items-center gap-1.5">
                                                                    {pend.faltas.map(falta => (
                                                                        <span key={falta} className="flex items-center gap-1 text-[10px] font-black text-red-700 dark:text-red-400">
                                                                            <CircleAlert size={11} /> falta {ROTULO_FALTA[falta]}
                                                                        </span>
                                                                    ))}
                                                                    {pend.faltas.includes('boleto_vigor') && (
                                                                        <a
                                                                            href={`#/financeiro/faturas?busca=${encodeURIComponent(f.uc_ref || parada.uc_codigo)}`}
                                                                            target="_blank" rel="noopener noreferrer"
                                                                            className="flex items-center gap-1 px-2 py-0.5 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-[10px] font-bold text-gray-600 dark:text-gray-300"
                                                                        >
                                                                            <ExternalLink size={10} /> Emitir
                                                                        </a>
                                                                    )}
                                                                    {pend.faltas.includes('fatura_equatorial') && (
                                                                        <label className="flex items-center gap-1 px-2 py-0.5 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-[10px] font-bold text-gray-600 dark:text-gray-300 cursor-pointer">
                                                                            {subindo === String(f.id)
                                                                                ? <Loader2 size={10} className="animate-spin" />
                                                                                : <Upload size={10} />} Anexar PDF
                                                                            <input
                                                                                type="file" accept="application/pdf" className="hidden"
                                                                                onChange={e => {
                                                                                    const a = e.target.files?.[0];
                                                                                    if (a) void subirArquivo('fatura', a, String(f.id));
                                                                                    e.target.value = '';
                                                                                }}
                                                                            />
                                                                        </label>
                                                                    )}
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {!avaliacao.completo && (
                                            <div>
                                                <label className="block text-[10px] font-black uppercase tracking-[0.12em] text-red-600 dark:text-red-400 mb-1.5">
                                                    Por que mandar mesmo assim?
                                                </label>
                                                <textarea
                                                    value={rascunho.justificativa}
                                                    onChange={e => setRascunho(r => ({ ...r, justificativa: e.target.value }))}
                                                    rows={2}
                                                    placeholder={avaliacao.faltaNaRecente
                                                        ? 'A fatura mais recente está sem material — explique como o cobrador vai receber.'
                                                        : 'Ex.: cliente vai negociar o total, boletos antigos entram no parcelamento.'}
                                                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-800 text-xs outline-none focus:ring-2 focus:ring-red-400 resize-none"
                                                />
                                                <p className={`mt-1 text-[10px] ${justificativaOk ? 'text-gray-400' : 'text-red-600 font-semibold'}`}>
                                                    {rascunho.justificativa.trim().length}/{MIN_JUSTIFICATIVA} caracteres mínimos
                                                </p>
                                            </div>
                                        )}
                                    </section>

                                    {/* ── 3. TERMO ────────────────────────────────── */}
                                    <section className={`rounded-xl border p-4 space-y-2.5 ${
                                        todosComTermo || rascunho.termoSituacao === 'anexado'
                                            ? 'border-gray-200 dark:border-gray-700'
                                            : 'border-red-200 dark:border-red-900/60'}`}>
                                        <Rotulo
                                            icone={<Stamp size={12} />}
                                            estado={todosComTermo || rascunho.termoSituacao === 'anexado' ? 'ok' : 'grave'}
                                        >
                                            3 · Termo de adesão
                                        </Rotulo>

                                        <ul className="space-y-1.5">
                                            {ucsAbertas.map(u => {
                                                const t = termoDaUc(u.uc_codigo);
                                                return (
                                                    <li key={u.uc_codigo} className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                                                        {grupo && (
                                                            <span className="min-w-[112px] text-[11px] font-black text-gray-700 dark:text-gray-200 tabular-nums">
                                                                UC {u.uc_codigo}
                                                            </span>
                                                        )}
                                                        {t?.url ? (
                                                            <>
                                                                <a href={t.url} target="_blank" rel="noopener noreferrer"
                                                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#5F6C37]/10 text-[#5F6C37] dark:text-[#9db068] text-[11px] font-bold hover:bg-[#5F6C37]/20 transition-colors">
                                                                    <FileText size={12} /> Ver termo
                                                                    {t.dataAssinatura && <> · assinado em {dataBr(t.dataAssinatura)}</>}
                                                                </a>
                                                                <span className="text-[10px] text-gray-400">encontrado no sistema</span>
                                                            </>
                                                        ) : (
                                                            <span className="flex items-center gap-1 text-[11px] font-black text-red-700 dark:text-red-400">
                                                                <CircleAlert size={12} /> Termo de adesão não encontrado
                                                            </span>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>

                                        {!todosComTermo && (
                                            <>
                                                <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
                                                    Procurei no cadastro da UC, na negociação e no CPF/CNPJ do cliente. Se o cliente é
                                                    antigo e nunca assinou, responda <strong>Não existe termo</strong>; se você tem o PDF, anexe.
                                                </p>
                                                <div className="flex flex-wrap gap-2">
                                                    <button
                                                        onClick={() => setRascunho(r => ({ ...r, termoSituacao: 'sem_termo', termoUrl: null }))}
                                                        className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-colors ${
                                                            rascunho.termoSituacao === 'sem_termo'
                                                                ? 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900'
                                                                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                                                    >
                                                        Não existe termo
                                                    </button>
                                                    <label className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold cursor-pointer transition-colors ${
                                                        rascunho.termoSituacao === 'anexado'
                                                            ? 'bg-[#5F6C37] text-white'
                                                            : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}>
                                                        {subindo === 'termo' ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                                                        {rascunho.termoSituacao === 'anexado' ? 'Termo anexado' : 'Anexar termo'}
                                                        <input
                                                            type="file" accept="application/pdf" className="hidden"
                                                            onChange={e => {
                                                                const a = e.target.files?.[0];
                                                                if (a) void subirArquivo('termo', a);
                                                                e.target.value = '';
                                                            }}
                                                        />
                                                    </label>
                                                    {rascunho.termoSituacao === 'anexado' && rascunho.termoUrl && (
                                                        <button
                                                            onClick={() => void abrirAnexoConferencia(rascunho.termoUrl!)
                                                                .catch((e: any) => addToast(e?.message || 'Não consegui abrir o anexo.', 'error'))}
                                                            className="flex items-center gap-1 px-2 py-1.5 text-[11px] font-bold text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:underline underline-offset-2"
                                                        >
                                                            <Paperclip size={12} /> ver o que subi
                                                        </button>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </section>

                                    {/* Ação da parada */}
                                    <div className="flex items-center gap-3 pt-1 pb-2 flex-wrap">
                                        <button
                                            onClick={confirmarParada}
                                            disabled={!podeConfirmar || salvando}
                                            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#5F6C37] hover:bg-[#4a5429] text-white text-sm font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            {salvando ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} strokeWidth={3} />}
                                            Conferir parada {parada.posicao}
                                        </button>
                                        {!podeConfirmar && (
                                            <span className="text-[11px] text-red-600 dark:text-red-400 font-bold">
                                                Para conferir, falta {pendenciasDaParada({
                                                    endereco_situacao: rascunho.enderecoSituacao,
                                                    material_situacao: materialSituacao,
                                                    material_justificativa: rascunho.justificativa,
                                                    termo_situacao: rascunho.termoSituacao,
                                                }).map(m => ROTULO_PENDENCIA[m]).join(', ')}
                                            </span>
                                        )}
                                        {indice < paradas.length - 1 && (
                                            <button
                                                onClick={() => setIndice(i => i + 1)}
                                                className="ml-auto flex items-center gap-1 text-[11px] font-bold text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                            >
                                                próxima <ChevronRight size={13} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Rodapé — a assinatura */}
                <footer className="shrink-0 px-5 py-3.5 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3 bg-gray-50/80 dark:bg-gray-900/60">
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 space-x-2">
                        {jaLiberada ? (
                            <span className="inline-flex items-center gap-1.5 font-bold text-[#5F6C37] dark:text-[#9db068]">
                                <ShieldCheck size={13} /> Rota já liberada
                            </span>
                        ) : situacao.liberavel ? (
                            <span className="font-bold text-[#5F6C37] dark:text-[#9db068]">
                                Tudo conferido. O cobrador vai receber endereço, boleto e contrato.
                            </span>
                        ) : (
                            <span>Ainda travam: paradas <strong className="text-gray-700 dark:text-gray-200 tabular-nums">{situacao.posicoesPendentes.join(', ') || '—'}</strong></span>
                        )}
                        {!carregando && comFalta > 0 && (
                            <span className="inline-flex items-center gap-1 font-bold text-red-600 dark:text-red-400">
                                <AlertTriangle size={12} /> {comFalta} com algo faltando
                            </span>
                        )}
                    </p>
                    <button
                        onClick={liberar}
                        disabled={!situacao.liberavel || liberando || jaLiberada || mexendo}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-black tracking-tight transition-all hover:scale-[1.02] disabled:opacity-30 disabled:hover:scale-100 disabled:cursor-not-allowed"
                    >
                        {liberando ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />}
                        Liberar rota
                    </button>
                </footer>
            </div>
        </div>
    );
};

export default ConferenciaRotaModal;
